import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ConfigProvider, theme as antdTheme } from "antd";

export type ThemeMode = "light" | "dark";

const STORAGE_KEY = "zs-theme";
const TRANSITION_CLASS = "zs-theme-transition";

interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "light",
  setTheme: () => undefined,
  toggleTheme: () => undefined,
});

// Light is the default experience; dark is only used once the user picks it.
const readStoredTheme = (): ThemeMode => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
};

const applyThemeToDocument = (theme: ThemeMode) => {
  const root = document.documentElement;
  root.setAttribute("data-theme", theme);
  root.classList.toggle("dark", theme === "dark");
};

// antd computes its own palette, so it receives concrete values that match tokens.css.
const antdTokens = {
  light: {
    colorPrimary: "#4F46E5",
    colorInfo: "#4F46E5",
    colorSuccess: "#0D9478",
    colorWarning: "#C47008",
    colorError: "#D63031",
    colorBgLayout: "#F5F6FA",
    colorBorder: "#CED4E2",
    colorBorderSecondary: "#E4E7F0",
    colorText: "#0F172A",
    colorTextSecondary: "#3D485E",
  },
  dark: {
    colorPrimary: "#6366F1",
    colorInfo: "#6366F1",
    colorSuccess: "#34D3AA",
    colorWarning: "#F5B43C",
    colorError: "#F87171",
    colorBgBase: "#131825",
    colorBgLayout: "#0A0D16",
    colorBgContainer: "#131825",
    colorBgElevated: "#191F2F",
    colorBorder: "#364058",
    colorBorderSecondary: "#262E42",
    colorText: "#E8ECF4",
    colorTextSecondary: "#B8C0D2",
  },
};

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setThemeState] = useState<ThemeMode>(readStoredTheme);

  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  const setTheme = useCallback((next: ThemeMode) => {
    const root = document.documentElement;
    root.classList.add(TRANSITION_CLASS);
    window.setTimeout(() => root.classList.remove(TRANSITION_CLASS), 260);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage can be unavailable (private mode); the theme still applies for this session.
    }
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);

  const antdConfig = useMemo(
    () => ({
      algorithm: theme === "dark" ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: {
        ...antdTokens[theme],
        borderRadius: 10,
        borderRadiusLG: 14,
        borderRadiusSM: 6,
        fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        fontSize: 14,
        controlHeight: 40,
        wireframe: false,
      },
    }),
    [theme]
  );

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={antdConfig}>{children}</ConfigProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
